import { AustralianTaxInfo, OverseasTaxInfo2 } from '@cmctechnology/phoenix-stockbroking-api-client';
import { useValidator, Validators } from '@cmctechnology/phoenix-stockbroking-web-design';
import { useTranslation } from 'react-i18next';
import { createKeyValueTranslatedToOptionList } from '../common/mappings';

import { COUNTRIES_EXCEPT_AUS, COUNTRY_CODE_USA } from '../constants/countryConstants';
import { FIELD_VALIDATION_MESSAGES } from '../constants/validationConstants';
import { AUS_TAX_EXEMPTION_REASONS, OVERSEAS_TAX_EXEMPTION_REASONS, UNABLE_TO_OBTAIN_TIN_REASON } from '../models/profile';
import { taxValidator } from '../validators/taxValidator';
import { US_TAX_MAX_LENGTH, US_TAX_TYPE } from '../constants/taxConstants';

const US_TAX_MODULUS = 10;
const unformatUsTin = (value: string) => value.replace(/-/g, '');
export const sanitizeUsTin = (value: string) => unformatUsTin(value).slice(0, US_TAX_MAX_LENGTH);
const isValidUsTinOrEin = (value: string): boolean => {
  const input = sanitizeUsTin(value);
  // eslint-disable-next-line unicorn/prefer-spread
  const digits = input.split('').map(Number);
  const isSequentialAsc = digits.every(
    (d, i, arr) => i === 0 || arr[i] === (arr[i - 1] + 1) % US_TAX_MODULUS // wrap-around handles 012345678
  );

  const isSequentialDesc = digits.every(
    (d, i, arr) => i === 0 || arr[i] === (arr[i - 1] + US_TAX_MAX_LENGTH) % US_TAX_MODULUS // 9-step backward mod 10 handles 987654321
  );

  if (isSequentialAsc || isSequentialDesc) {
    return false;
  }

  return true;
};

export const useTaxValidators = (australianTaxInfo: AustralianTaxInfo, overseasTaxInfo: OverseasTaxInfo2) => {
  const { t } = useTranslation();
  // TFN should be validated upfront since it's an optional field and only validated once a text has been entered
  const tfnValidator = useValidator(australianTaxInfo.tfn, Validators.custom(taxValidator.isValidTfn, FIELD_VALIDATION_MESSAGES.Tfn.message(t)));

  const ausTaxExemptionReasonsSelectOptions = createKeyValueTranslatedToOptionList(AUS_TAX_EXEMPTION_REASONS, t);
  const ausTaxExemptionReasonValidator = useValidator(
    ausTaxExemptionReasonsSelectOptions.find((x) => x.value === australianTaxInfo.taxExemptionReason),
    Validators.custom((value) => {
      return !!value;
    }, t('Required'))
  );

  const countriesExceptAusSelectOptions = createKeyValueTranslatedToOptionList(COUNTRIES_EXCEPT_AUS, t);
  const countryValidator = useValidator(
    countriesExceptAusSelectOptions.find((x) => x.value === overseasTaxInfo.country),
    Validators.required(t('Required'))
  );

  const tinValidator = useValidator(
    overseasTaxInfo.tin,
    Validators.combine([
      Validators.custom((value: string) => !!value, t('Required')),
      Validators.custom((value: string) => {
        const input = sanitizeUsTin(value);
        if (countryValidator.value?.value === COUNTRY_CODE_USA) {
          return input.length === US_TAX_MAX_LENGTH;
        }

        return true;
      }, t('The US TIN must be 9 digits')),
      Validators.custom((value: string) => {
        const input = sanitizeUsTin(value);
        if (countryValidator.value?.value === COUNTRY_CODE_USA) {
          return /^\d+$/.test(input);
        }

        return true;
      }, t('The US TIN must contain numeric characters only')),
      Validators.custom((value: string) => {
        const input = sanitizeUsTin(value);
        if (countryValidator.value?.value === COUNTRY_CODE_USA) {
          if (/^(\d)\1{8}$/.test(input)) return false;

          if (!isValidUsTinOrEin(value)) return false;

          return true;
        }

        return true;
      }, t('The US TIN is invalid, please provide a valid US TIN'))
    ])
  );

  const overseasTaxExemptionReasonsSelectOptions = createKeyValueTranslatedToOptionList(OVERSEAS_TAX_EXEMPTION_REASONS, t);
  const overseasTaxExemptionReasonValidator = useValidator(
    overseasTaxExemptionReasonsSelectOptions.find((x) => x.value === overseasTaxInfo.taxExemptionReason),
    Validators.custom((value) => {
      return !!value;
    }, t('Required'))
  );

  const overseasTaxExemptionExplanationValidator = useValidator(
    overseasTaxInfo.taxExemptionExplanation,
    Validators.custom((value) => {
      return overseasTaxExemptionReasonValidator.value?.value !== UNABLE_TO_OBTAIN_TIN_REASON || !!value;
    }, t('Required'))
  );

  const tsxTypeSelectOptions = createKeyValueTranslatedToOptionList(US_TAX_TYPE, t);
  const taxTypeUsValidator = useValidator(
    tsxTypeSelectOptions.find((x) => x.value === overseasTaxInfo.tinType),
    Validators.required(t('Required'))
  );

  return {
    tfnValidator,
    ausTaxExemptionReasonValidator,
    countryValidator,
    tinValidator,
    overseasTaxExemptionReasonValidator,
    overseasTaxExemptionExplanationValidator,
    taxTypeUsValidator
  };
};
